import { createFileRoute, Outlet, useLocation, useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useCan } from '../../contexts/PermissionsContext'
import { Button } from '../../components/ui/button'
import { LEVELS, PERIODS, useGroupMutations, useGroupSummary, type Level, type Period } from '../../api/groupSteering'
import { useState } from 'react'
import { PageHeader } from '../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { useLocale } from '../../contexts/LocaleContext'
import { groupBreadcrumbs } from '../../components/group-steering/breadcrumbs'
import { langOf, text } from '../../components/group-steering/format'

export type GroupSearch = {
    period?: Period
    level?: Level
    family?: string
    kpi?: string
    selected?: string
    q?: string
}

/**
 * The group steering space (spec 2026-09-11-01): its own sidebar (rendered
 * by AppSidebar for this path family), a header with the period and level,
 * and the routed page. Every reading state lives in the URL.
 */
export const Route = createFileRoute('/$org/group')({
    component: GroupLayout,
    validateSearch: (search: Record<string, unknown>): GroupSearch => ({
        period: PERIODS.includes(search.period as Period) ? (search.period as Period) : undefined,
        level: LEVELS.includes(search.level as Level) ? (search.level as Level) : undefined,
        family: search.family ? String(search.family) : undefined,
        kpi: search.kpi ? String(search.kpi) : undefined,
        selected: search.selected ? String(search.selected) : undefined,
        q: search.q ? String(search.q) : undefined,
    }),
})

function GroupLayout() {
    const { org } = Route.useParams()
    const { t } = useTranslation('group-steering')
    const navigate = useNavigate()
    const search = useSearch({ from: '/$org/group' })
    const period: Period = search.period ?? '90d'
    const level: Level = search.level ?? 'agency'
    const summary = useGroupSummary(org, period, level)
    const canRecompute = useCan('configuration.write')
    const { recompute } = useGroupMutations(org)
    const [queued, setQueued] = useState(false)
    const [landed, setLanded] = useState<'done' | 'late' | null>(null)
    const { locale } = useLocale()
    const { pathname } = useLocation()
    // The family the consolidated view shows: the URL's, else the first one.
    const families = summary.data?.families ?? []
    const shownFamily = families.find((f) => f.code === search.family) ?? families[0]
    useBreadcrumbs(
        groupBreadcrumbs({
            org,
            pathname,
            home: t('common:nav.home'),
            space: t('space.title'),
            navLabel: (key) => t(`nav.${key}`),
            familyLabel: shownFamily ? text(shownFamily.label, langOf(locale)) : undefined,
        })
    )

    const setSearch = (patch: Partial<typeof search>) =>
        navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }) as never, replace: true })

    const status = summary.error as { status?: number; code?: string } | null
    const notFound = !!status && (status.status === 404 || (status as { response?: { status?: number } }).response?.status === 404)

    return (
        <div className="space-y-4" data-testid="group-space">
            <div className="flex flex-wrap items-center gap-2" data-testid="group-header">
                <div className="inline-flex rounded-md border p-0.5" role="radiogroup" aria-label={t('period.label')}>
                    {PERIODS.map((p) => (
                        <button
                            key={p}
                            type="button"
                            role="radio"
                            aria-checked={period === p}
                            onClick={() => setSearch({ period: p })}
                            className={`px-2 py-1 text-xs rounded ${period === p ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid={`group-period-${p}`}
                        >
                            {t(`period.${p}`)}
                        </button>
                    ))}
                </div>
                <div className="inline-flex rounded-md border p-0.5" role="radiogroup" aria-label={t('level.label')}>
                    {LEVELS.map((l) => {
                        const disabled = l === 'portfolio' && !(summary.data?.portfolios_allowed ?? false)
                        return (
                            <button
                                key={l}
                                type="button"
                                role="radio"
                                aria-checked={level === l}
                                disabled={disabled}
                                title={disabled ? t('level.portfolio_disabled') : undefined}
                                onClick={() => setSearch({ level: l, selected: undefined })}
                                className={`px-2 py-1 text-xs rounded ${level === l ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                data-testid={`group-level-${l}`}
                            >
                                {t(`level.${l}`)}
                            </button>
                        )
                    })}
                </div>
                {canRecompute && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="ml-auto"
                        disabled={recompute.isPending}
                        onClick={async () => {
                            setQueued(true)
                            const landed = await recompute.mutateAsync()
                            setQueued(false)
                            setLanded(landed ? 'done' : 'late')
                        }}
                        data-testid="group-recompute"
                    >
                        {recompute.isPending ? t('space.recomputing') : t('space.recompute')}
                    </Button>
                )}
            </div>
            {queued && <p className="text-xs text-muted-foreground" data-testid="group-recompute-queued">{t('space.recompute_queued')}</p>}
            {landed === 'done' && <p className="text-xs text-muted-foreground" data-testid="group-recompute-done">{t('space.recompute_done')}</p>}
            {landed === 'late' && <p className="text-xs text-muted-foreground" data-testid="group-recompute-late">{t('space.recompute_late')}</p>}
            {notFound ? (
                <div data-testid="group-empty">
                    <PageHeader
                        icon={SECTION_ICONS.group_steering}
                        title={canRecompute ? t('space.no_snapshot_title') : t('space.no_console_title')}
                        description={canRecompute ? t('space.no_snapshot_body') : t('space.no_console_body')}
                        data-testid="group-empty-title"
                    />
                </div>
            ) : (
                <Outlet />
            )}
        </div>
    )
}
